<template>
  <v-row>
    <v-col class="text-end pa-1">
      <v-icon-btn
        color="primary"
        icon="$plus"
        variant="text"
        size="small"
        :disabled="addPermissionBtnDisabled"
        @click="addPermission"
      />
    </v-col>
    <v-col cols="12">
      <v-form class="permission-form">
        <PermissionRow
          v-for="(permission, index) in permissions"
          :key="`permission-${index}`"
          :permission="permission"
          :allPermissions="permissions"
          :showDivider="index > 0"
          :hasSucoInPermissions="hasSucoInPermissions"
          @permissionChanged="permissionChanged($event, index)"
          @deletePermission="deletePermission(permission.roleId)"
          :selectedSubcontractor="selectedSubcontractor"
        />
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
import PermissionRow from '@/components/common/user/PermissionRow';
import { isEqual } from 'lodash';
import constants from '@/config/constants';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'UserPermissionsDialog',
  components: {
    PermissionRow
  },
  emits: ['permissionChanged', 'closeDialog', 'permissionButtonDisabled'],
  setup() {
    const { selectedContext } = usePermissions();
    return { selectedContext };
  },
  props: {
    permissionsDialog: {
      type: Object,
      required: false
    },
    selectedSubcontractor: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data() {
    return {
      permissions: [],
      isLoading: false,
      payload: {}
    };
  },
  computed: {
    existingPermissions() {
      return this.permissionsDialog.user.permissions ?? [];
    },
    removePermissions() {
      if (!this.existingPermissions.length) return [];
      const removePermissions = [];
      this.existingPermissions.forEach((existingPermission) => {
        const existingPermissionConvertedPath =
          this.convertedPermissionPath(existingPermission);

        const isOldPermissionStillExist = this.permissions.find(
          (permission) =>
            existingPermission.role?.id === permission.roleId &&
            permission.scopes.includes(existingPermissionConvertedPath)
        );
        if (!isOldPermissionStillExist) {
          removePermissions.push(existingPermission.id);
        }
      });
      return removePermissions;
    },
    convertedPermissions() {
      const convertedPermissions = [];
      this.permissions.forEach((permission) => {
        permission.scopes.forEach((scope) => {
          convertedPermissions.push({
            path: scope,
            roleId: permission.roleId
          });
        });
      });

      return convertedPermissions;
    },
    hasSucoInPermissions() {
      return this.permissions
        .flatMap((p) => p.scopes)
        .some((s) => this.isSuCoPermission(s));
    },
    addPermissionBtnDisabled() {
      const disableForSucoRoleInDepot =
        this.hasSucoInPermissions &&
        [
          constants.scopeTypes.DISTRIBUTION_CENTER,
          constants.scopeTypes.TENANT
        ].includes(this.selectedContext);

      return (
        this.permissions.some(
          ({ roleId, scopes }) => !roleId || !scopes.length
        ) || disableForSucoRoleInDepot
      );
    },
    isSubmitBtnDisabled() {
      const hasRemovePermissions = this.removePermissions.length > 0;
      const hasAddedPermissions =
        this.existingPermissions.length < this.convertedPermissions.length;
      const hasPermissionChanges = hasRemovePermissions || hasAddedPermissions;
      const hasPermissions = this.permissions.length > 0;
      const hasConvertedPermissions = this.convertedPermissions.length > 0;
      return (
        !hasPermissions || !hasPermissionChanges || !hasConvertedPermissions
      );
    },
    dialogTitle() {
      const { firstName, lastName } = this.permissionsDialog.user;
      if (firstName) {
        return `${firstName} ${lastName} ${this.$t('generic.permissions')}`;
      }
      return this.$t('generic.permissions');
    }
  },
  methods: {
    convertedPermissionPath({ scope, path }) {
      return scope === 'ADMIN' ? '' : path;
    },
    isSuCoPermission(permission) {
      return permission.split('.').length === 5;
    },
    setPermissions() {
      const permissions = [];
      this.existingPermissions.forEach((permission) => {
        const existingPermission = permissions.find(
          ({ roleId }) => roleId === permission.role.id
        );
        const convertedPath = this.convertedPermissionPath(permission);
        if (existingPermission) {
          existingPermission.scopes.push(convertedPath);
        } else {
          permissions.push({
            roleId: permission.role.id,
            scopes: [convertedPath]
          });
        }
      });
      this.permissions = permissions;
    },
    addPermission() {
      this.permissions.unshift({
        roleId: '',
        scopes: []
      });
    },
    permissionChanged(permission, index) {
      this.permissions[index] = permission;
    },
    deletePermission(roleId) {
      this.permissions = this.permissions.filter(
        (permission) => permission.roleId !== roleId
      );
    },
    closeDialog() {
      this.$emit('closeDialog');
      this.permissions = [];
      this.isLoading = false;
    },
    async updatePermissions() {
      this.isLoading = true;
      let addPermissions = this.convertedPermissions;
      if (this.existingPermissions.length) {
        addPermissions = this.convertedPermissions.filter((permission) => {
          return !this.existingPermissions.find((existingPermission) => {
            const convertedExistingPermission = {
              roleId: existingPermission.role.id,
              path: this.convertedPermissionPath(existingPermission)
            };
            return isEqual(permission, convertedExistingPermission);
          });
        });
      }
      this.payload = {
        permissions: {
          addPermissions,
          removePermissions: this.removePermissions
        },
        id: this.permissionsDialog.user.id
      };

      this.$emit('permissionChanged', this.payload);
    }
  },
  watch: {
    isSubmitBtnDisabled(newVal) {
      this.$emit('permissionButtonDisabled', newVal);
    },
    'permissions.length'(permissionsLength) {
      if (!permissionsLength && this.permissionsDialog.dialog) {
        this.addPermission();
      }
    },
    permissions: {
      handler() {
        this.updatePermissions();
      },
      deep: true
    }
  },
  mounted() {
    if (this.existingPermissions.length) {
      this.setPermissions();
    } else {
      this.addPermission();
    }
  },
  beforeUnmount() {
    this.closeDialog();
  }
};
</script>

<style scoped>
.permission-form {
  max-height: 50vh;
}
</style>
